import * as React from 'react';

import { translate } from '~l10n/client';
import { WorkspaceRole } from 'clients/app/src/api/workspaces/getWorkspaceMembers';

import { SmallButton } from '~shared/components/buttons';
import { Spinner } from '~shared/components/Spinner';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useGetWorkspaceCode } from '~teacher/hook/workspaces/code';
import { EffectiveOperationPermissionsMap } from '~teacher/hook/workspaces/permissionsV2/composePermissions/types';

import { useOnToggleEnabled } from './hooks';
import { useHandleResetCode } from './hooks';
import { InvitesDialogSectionToggle } from './InviteDialogSectionToggle/InvitesDialogSectionToggle';
import { InvitesDialogSectionControlCode } from './InvitesDialogSectionControlCode';
import { InvitesDialogSectionControlLink } from './InvitesDialogSectionControlLink';

interface InvitesDialogSectionProps {
  workspaceId: string;
  workspaceRole: WorkspaceRole;
}

export function InvitesDialogSection({
  workspaceId,
  workspaceRole,
}: InvitesDialogSectionProps) {
  const teacherApplicationContext = useTeacherApplicationContext();

  const {
    permissions: {
      turnOffInviteLink,
      resetInviteLink,
      viewInviteEditorLink,
      viewInviteViewerLink,
    },
    accountQuery,
    roleQuery,
  } = useWorkspacePermissionsV2({
    workspaceId,
  });

  const workspaceCodeQuery = useGetWorkspaceCode({
    workspaceId,
    workspaceRole,
  });

  const onToggleEnabled = useOnToggleEnabled({
    workspaceId,
    workspaceRole,
    canTurnOff: turnOffInviteLink,
  });

  const handleResetCode = useHandleResetCode({
    canResetInviteLink: resetInviteLink,
    workspaceRole,
  });

  if (!workspaceCodeQuery.data && !accountQuery.data && !roleQuery.data) {
    return <Spinner />;
  }

  const isOn = workspaceCodeQuery.data?.code !== undefined;

  return (
    <div className="flex flex-col gap-4 py-4">
      <InvitesDialogSectionToggle
        label={textMap.get(workspaceRole)?.title ?? ''}
        description={textMap.get(workspaceRole)?.description ?? ''}
        on={isOn}
        isDisabled={!turnOffInviteLink}
        onEnabledChange={onToggleEnabled}
      />
      {isOn ? (
        <>
          {canViewCode(workspaceRole, {
            viewInviteEditorLink,
            viewInviteViewerLink,
          }) ? (
            <>
              {teacherApplicationContext ===
              TeacherApplicationContext.Socrative ? (
                <InvitesDialogSectionControlLink
                  workspaceId={workspaceId}
                  workspaceRole={workspaceRole}
                />
              ) : null}
              <InvitesDialogSectionControlCode
                workspaceId={workspaceId}
                workspaceRole={workspaceRole}
              />
            </>
          ) : null}
          <div>
            <SmallButton
              onClick={handleResetCode}
              isDisabled={!resetInviteLink}
            >
              {translate('Reset')}
            </SmallButton>
          </div>
        </>
      ) : null}
    </div>
  );
}

const permissionsLookUpConfig: Map<
  WorkspaceRole,
  keyof EffectiveOperationPermissionsMap
> = new Map([
  [WorkspaceRole.Editor, 'viewInviteEditorLink' as const],
  [WorkspaceRole.Viewer, 'viewInviteViewerLink' as const],
]);

function canViewCode(
  workspaceRole: WorkspaceRole,
  permissions: Pick<
    EffectiveOperationPermissionsMap,
    'viewInviteEditorLink' | 'viewInviteViewerLink'
  >
) {
  const permission = permissionsLookUpConfig.get(workspaceRole);
  if (!permission) {
    return false;
  }
  return permissions[permission];
}

const textMap = new Map([
  [
    WorkspaceRole.Editor,
    {
      title: translate('Editor'),
      description: translate(
        'Can edit library content and invite other Editors and Viewers.'
      ),
    },
  ],
  [
    WorkspaceRole.Viewer,
    {
      title: translate('Viewer'),
      description: translate(
        'Can only view and export library content. Can view members and invite other Viewers.'
      ),
    },
  ],
]);
